import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import api, { formatApiError } from "@/lib/api";
import useVoiceRecorder from "@/lib/useVoiceRecorder";
import { LLM_REQUEST_TIMEOUT_MS, BYTES_PER_KB } from "@/lib/constants";

export default function Ingest() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [pastedAt, setPastedAt] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const voice = useVoiceRecorder();
  const [audioBlob, setAudioBlob] = useState(null);

  // Allow Cmd/Ctrl+V to drop an image straight into the ingest pipeline.
  // We only intercept when the clipboard carries an image; pasting text
  // into the textarea (or anywhere else) keeps its default behaviour.
  useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items || items.length === 0) return;
      for (const item of items) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (!file) continue;
          e.preventDefault();
          // Give the pasted blob a friendlier name than "image.png" so the
          // UI reads it cleanly when the OS doesn't provide one.
          const ext = (file.type.split("/")[1] || "png").replace("jpeg", "jpg");
          const named =
            file.name && file.name !== "image.png"
              ? file
              : new File([file], `colado-${Date.now()}.${ext}`, { type: file.type });
          setImageFile(named);
          setPastedAt(Date.now());
          setError("");
          setResult(null);
          return;
        }
      }
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, []);

  const runText = async () => {
    if (!text.trim() || processing) return;
    setProcessing("text");
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/ingest/text", { text }, { timeout: LLM_REQUEST_TIMEOUT_MS });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setProcessing(null);
    }
  };

  const runImage = async () => {
    if (!imageFile || processing) return;
    setProcessing("image");
    setError("");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", imageFile);
      const { data } = await api.post("/ingest/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: LLM_REQUEST_TIMEOUT_MS,
      });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setProcessing(null);
    }
  };

  const onVoiceToggle = async () => {
    if (voice.recording) {
      const blob = await voice.stop();
      setAudioBlob(blob);
    } else {
      setAudioBlob(null);
      await voice.start();
    }
  };

  const runAudio = async () => {
    if (!audioBlob || processing) return;
    setProcessing("audio");
    setError("");
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", audioBlob, "voice.webm");
      const { data } = await api.post("/ingest/audio?mode=ingest", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: LLM_REQUEST_TIMEOUT_MS,
      });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setProcessing(null);
    }
  };

  return (
    <div className="px-6 md:px-12 lg:px-16 py-12 md:py-16 max-w-5xl">
      <p className="label-cap">— {t("app.sidebar2.ingest")}</p>
      <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight mt-3" data-testid="ingest-title">
        {t("app.ingestTitle")}
      </h1>
      <p className="font-display-italic text-[var(--ink-soft)] mt-4 max-w-3xl text-lg leading-relaxed">
        {t("app.ingestSub")}
      </p>

      <div className="grid md:grid-cols-2 gap-px bg-[var(--border)] mt-12">
        {/* TEXT */}
        <section className="bg-[var(--bg)] p-8" data-testid="ingest-text-section">
          <p className="label-cap text-[var(--oxblood)]">— {t("app.ingestTextLabel")}</p>
          <textarea
            rows={8}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="line-input resize-none mt-4"
            placeholder={t("app.ingestTextPlaceholder")}
            data-testid="ingest-text-input"
          />
          <button
            onClick={runText}
            disabled={!text.trim() || processing !== null}
            className="btn-primary mt-6"
            data-testid="ingest-text-process"
          >
            {processing === "text" ? t("app.ingestProcessing") : t("app.ingestProcess")}
          </button>
        </section>

        {/* IMAGE */}
        <section className="bg-[var(--bg)] p-8" data-testid="ingest-image-section">
          <p className="label-cap text-[var(--oxblood)]">— {t("app.ingestImageLabel")}</p>
          <p className="text-xs text-[var(--ink-faint)] mt-2">{t("app.ingestImageHint")}</p>
          <p className="text-xs text-[var(--ink-faint)] mt-1">{t("app.ingestImagePasteHint")}</p>
          {/* Direct camera capture (mobile) — additive; upload input below keeps
              the "choose an existing photo" path (e.g. a saved receipt). */}
          <label
            className="btn-outline inline-flex items-center mt-6 mr-3 cursor-pointer"
            data-testid="ingest-image-camera"
          >
            {lang === "pt" ? "Tirar foto" : "Take photo"}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                setImageFile(e.target.files?.[0] ?? null);
                setPastedAt(null);
              }}
            />
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              setImageFile(e.target.files?.[0] ?? null);
              setPastedAt(null);
            }}
            className="block mt-4 text-sm"
            data-testid="ingest-image-input"
          />
          {imageFile && (
            <p className="text-xs text-[var(--ink-soft)] mt-2" data-testid="ingest-image-meta">
              {pastedAt ? `${t("app.ingestImagePasted")} · ` : ""}
              {imageFile.name} · {Math.round(imageFile.size / BYTES_PER_KB)} KB
            </p>
          )}
          <button
            onClick={runImage}
            disabled={!imageFile || processing !== null}
            className="btn-primary mt-6"
            data-testid="ingest-image-process"
          >
            {processing === "image" ? t("app.ingestProcessing") : t("app.ingestProcess")}
          </button>
        </section>

        {/* AUDIO */}
        <section className="bg-[var(--bg)] p-8 md:col-span-2" data-testid="ingest-audio-section">
          <p className="label-cap text-[var(--oxblood)]">— {t("app.ingestAudioLabel")}</p>
          <p className="text-xs text-[var(--ink-faint)] mt-2">{t("app.ingestAudioHint")}</p>
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              onClick={onVoiceToggle}
              disabled={voice.busy || processing !== null}
              className={voice.recording ? "btn-primary" : "btn-outline"}
              data-testid="ingest-audio-record"
            >
              {voice.recording ? "■ " + t("app.ingestStop") : "● " + t("app.ingestRecord")}
            </button>
            {audioBlob && !voice.recording && (
              <>
                <span className="label-cap text-[var(--ink-faint)]">
                  · {Math.round(audioBlob.size / BYTES_PER_KB)} KB
                </span>
                <audio controls src={URL.createObjectURL(audioBlob)} className="ml-2" />
                <button
                  onClick={runAudio}
                  disabled={processing !== null}
                  className="btn-primary"
                  data-testid="ingest-audio-process"
                >
                  {processing === "audio" ? t("app.ingestProcessing") : t("app.ingestProcess")}
                </button>
              </>
            )}
          </div>
        </section>
      </div>

      {error && (
        <p className="text-[var(--error)] text-sm mt-6" data-testid="ingest-error">
          {error}
        </p>
      )}

      {result && (
        <div className="mt-12 card-flat" data-testid="ingest-result">
          <p className="label-cap text-[var(--oxblood)]">— {t("app.ingestResult")}</p>
          {result.text && (
            <p className="mt-4 font-display-italic text-[var(--ink-soft)]">«{result.text}»</p>
          )}
          <div className="mt-6 flex flex-wrap gap-8">
            <Stat label={t("app.ingestEvents")} value={result.counts?.events ?? 0} />
            <Stat label={t("app.ingestTasks")} value={result.counts?.tasks ?? 0} />
            <Stat label={t("app.ingestShopping")} value={result.counts?.shopping ?? 0} />
          </div>
          {(result.counts?.events || result.counts?.tasks || result.counts?.shopping) > 0 && (
            <button
              onClick={() => navigate("/app/proposals")}
              className="btn-outline mt-8"
              data-testid="ingest-see-proposals"
            >
              {t("app.ingestSeeProposals")} →
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="font-display text-4xl font-light">{value}</p>
      <p className="label-cap text-[var(--ink-soft)] mt-1">{label}</p>
    </div>
  );
}
